// Imaginær skrivning — ord for ord, zen-UI uden WPM/tællere.
//
// Tre faser via presentation.phase:
//   visible   — ord står; skriv når du er klar (ingen «klar»-knap)
//   flash     — ord vises flashMs (fast), derefter skriv
//   dictation — kun lyd; skrevne bogstaver vises undervejs

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  KeyboardLayoutId,
  RoundPresentation,
  RoundResult,
  Trin,
  UserProfile,
} from '../../types';
import type { PreparedRound } from '../../lib/lessonGenerator';
import { getDictationAudio } from '../../lib/audio';
import { dictationAudioLocaleForTrin } from '../../lib/dictationAudioLocale';
import { isDictationAudioHintChar } from '../../lib/dictationDisplay';
import { playSuccessChime, primeSuccessChime } from '../../lib/audio/successChime';
import { MENTAL_IMAGERY_FLASH_MS } from '../../lib/mentalImagery';
import { useRoundInput } from '../../lib/useRoundInput';
import { defaultPreferences } from '../../lib/storage';
import { activeWordIndex, splitWords, wordIndexAtCursor } from '../../lib/wordSpans';
import Keyboard from '../Keyboard';
import SpaceAdvanceHint from '../SpaceAdvanceHint';

interface ImageryRoundProps {
  round: PreparedRound;
  trin: Trin;
  keyboardLayout: KeyboardLayoutId;
  user: UserProfile;
  strictCase?: boolean;
  allowAltLayer?: boolean;
  onFinish: (r: RoundResult) => void;
}

type ImageryPresentation = Extract<RoundPresentation, { kind: 'imagery' }>;

export default function ImageryRound({
  round,
  trin,
  keyboardLayout,
  user,
  strictCase = false,
  allowAltLayer = false,
  onFinish,
}: ImageryRoundProps) {
  const presentation = round.template.presentation as ImageryPresentation;
  const phase = presentation.phase;
  const flashMs = presentation.flashMs ?? MENTAL_IMAGERY_FLASH_MS;

  const targetText = round.targetText;
  const mode = round.template.mode;
  const prefs = user.preferences ?? defaultPreferences();
  const audioLocale = dictationAudioLocaleForTrin(trin, user);

  const audio = useMemo(() => getDictationAudio(), []);
  const [voicesReady, setVoicesReady] = useState(false);
  const [awaitingSpace, setAwaitingSpace] = useState(false);
  /** Blink-runde: vis det rigtige ord kort, derefter kun prikker + grøn feedback. */
  const [flashShowWord, setFlashShowWord] = useState(false);
  const [dictationStarted, setDictationStarted] = useState(phase !== 'dictation');
  const plingedAtRef = useRef<number | null>(null);
  const lastWordIdxRef = useRef(-1);
  const lastMistakesRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void audio.waitForVoices().then(() => {
      if (!cancelled) setVoicesReady(true);
    });
    return () => {
      cancelled = true;
      audio.cancel();
    };
  }, [audio]);

  const audioAvailable =
    prefs.audioEnabled && voicesReady && audio.isAvailable();

  const input = useRoundInput({
    targetText,
    mode,
    roundId: round.template.id,
    roundTitle: round.template.title,
    presentationKind: 'imagery',
    strictCase,
    allowAltLayer,
    keyboardLayoutId: keyboardLayout,
    onFinish,
    enabled: phase === 'dictation' ? dictationStarted : true,
    onAdvance: ({ cursor, char, completed }) => {
      if (char === ' ') {
        setAwaitingSpace(false);
        plingedAtRef.current = null;
        if (!completed && phase === 'dictation') speakWordAt(cursor);
        return;
      }
      if (completed) {
        if (prefs.audioEnabled) playSuccessChime();
        return;
      }
      const needsSpace =
        cursor < targetText.length && targetText[cursor] === ' ';
      if (needsSpace) {
        plingedAtRef.current = cursor;
        setAwaitingSpace(true);
      } else {
        setAwaitingSpace(false);
      }
    },
  });

  const words = useMemo(() => splitWords(targetText), [targetText]);
  const currentWordIdx = activeWordIndex(
    words,
    input.cursor,
    awaitingSpace,
  );
  const currentWord = currentWordIdx >= 0 ? words[currentWordIdx] : null;

  const speakWordAt = useCallback(
    (cursor: number) => {
      if (!audioAvailable || phase !== 'dictation') return;
      const idx = wordIndexAtCursor(words, cursor);
      const word = idx >= 0 ? words[idx] : words[0];
      if (!word) return;
      audio.cancel();
      void audio.speak(word.text, {
        rate: prefs.dictationRate,
        voiceId: prefs.dictationVoiceId,
        audioLocale,
      });
    },
    [
      audio,
      audioAvailable,
      words,
      phase,
      prefs.dictationRate,
      prefs.dictationVoiceId,
      audioLocale,
    ],
  );

  const handleDictationStart = useCallback(() => {
    primeSuccessChime();
    setDictationStarted(true);
    speakWordAt(0);
  }, [speakWordAt]);

  useEffect(() => {
    if (phase !== 'dictation' || dictationStarted) return;
    if (voicesReady && !audioAvailable) {
      setDictationStarted(true);
    }
  }, [phase, dictationStarted, voicesReady, audioAvailable]);

  // Blink-runde: ord 1s → prikker. Gentagelser (lad lad) springer blink over.
  useEffect(() => {
    if (phase !== 'flash' || awaitingSpace || !currentWord) return undefined;

    const wordChanged = currentWordIdx !== lastWordIdxRef.current;
    const mistakeAdded = input.mistakes > lastMistakesRef.current;
    const prevWordText =
      currentWordIdx > 0 ? words[currentWordIdx - 1]?.text : undefined;
    const isConsecutiveRepeat =
      wordChanged &&
      prevWordText !== undefined &&
      currentWord.text === prevWordText;

    lastWordIdxRef.current = currentWordIdx;
    lastMistakesRef.current = input.mistakes;

    if (wordChanged && isConsecutiveRepeat) {
      setFlashShowWord(false);
      return undefined;
    }

    const shouldFlash =
      (mistakeAdded && !wordChanged) ||
      (wordChanged && !isConsecutiveRepeat);

    if (shouldFlash) {
      setFlashShowWord(true);
      const id = window.setTimeout(() => setFlashShowWord(false), flashMs);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [
    phase,
    currentWordIdx,
    currentWord,
    input.mistakes,
    flashMs,
    awaitingSpace,
    words,
  ]);

  const handleRepeatAudio = useCallback(() => {
    if (currentWord) speakWordAt(currentWord.start);
    else speakWordAt(input.cursor);
  }, [currentWord, speakWordAt, input.cursor]);

  const phaseLabel =
    phase === 'visible'
      ? 'Se og forestil'
      : phase === 'flash'
        ? 'Kort blink'
        : 'Lyt indeni';

  const showReferenceWord =
    currentWord !== null &&
    !awaitingSpace &&
    (phase === 'visible' || (phase === 'flash' && flashShowWord));

  const showTypedFeedback =
    currentWord !== null &&
    (phase === 'visible'
      ? !awaitingSpace
      : phase === 'flash'
        ? !flashShowWord
        : !awaitingSpace);

  if (phase === 'dictation' && !dictationStarted) {
    return (
      <ZenShell phaseLabel={phaseLabel}>
        <div className="flex min-h-[16rem] flex-col items-center justify-center gap-5 px-6 py-10 text-center">
          <p className="text-lg font-medium text-strong">
            Sæt fingrene på hjemmerækken
          </p>
          <p className="max-w-sm text-sm leading-relaxed text-muted">
            {audioAvailable
              ? 'Du hører hvert ord én ad gangen. Forestil bevægelsen, før du taster.'
              : 'Ingen stemme tilgængelig — ordene vises som prikker mens du skriver.'}
          </p>
          <button
            type="button"
            autoFocus
            onClick={handleDictationStart}
            className="rounded-full border border-stone-300 bg-stone-50 px-8 py-3 text-sm font-medium text-stone-800 transition hover:bg-stone-100 dark:border-stone-600 dark:bg-stone-800/60 dark:text-stone-100 dark:hover:bg-stone-800"
          >
            Begynd
          </button>
        </div>
        <ZenKeyboard
          layoutId={keyboardLayout}
          unlockedKeys={trin.unlockedKeys}
          enabled={false}
        />
      </ZenShell>
    );
  }

  return (
    <ZenShell phaseLabel={phaseLabel}>
      <div className="flex min-h-[16rem] flex-col items-center justify-center gap-4 px-6 py-10 text-center">
        {currentWord ? (
          awaitingSpace ? (
            <>
              {phase === 'flash' ? (
                <TypedFeedback
                  word={currentWord.text}
                  wordStart={currentWord.start}
                  cursor={input.cursor}
                  correctMask={input.correctMask}
                  showReferenceAbove={false}
                />
              ) : (
                <p
                  className="font-mono text-5xl font-light tracking-wide text-emerald-700/90 dark:text-emerald-300/90"
                  aria-live="polite"
                >
                  {currentWord.text}
                </p>
              )}
              <SpaceAdvanceHint zen />
            </>
          ) : (
            <>
              {showReferenceWord && (
                <p
                  className="font-mono text-5xl font-light tracking-wide text-stone-800 dark:text-stone-100"
                  aria-live="polite"
                >
                  {currentWord.text}
                </p>
              )}
              {showTypedFeedback && (
                <TypedFeedback
                  word={currentWord.text}
                  wordStart={currentWord.start}
                  cursor={input.cursor}
                  correctMask={input.correctMask}
                  showReferenceAbove={showReferenceWord}
                  audioHints={phase === 'dictation' && audioAvailable}
                />
              )}
              {phase === 'visible' && showReferenceWord && (
                <p className="max-w-xs text-xs leading-relaxed text-faint">
                  Skriv når du er klar — dine bogstaver vises nedenunder.
                </p>
              )}
              {phase === 'flash' && showTypedFeedback && !flashShowWord && (
                <p className="max-w-xs text-xs leading-relaxed text-faint">
                  Skriv fra det indre billede — grønne bogstaver erstatter prikkerne.
                </p>
              )}
              {input.lastPress?.correct === false && (
                <p className="text-xs text-stone-500 dark:text-stone-400">
                  {phase === 'flash'
                    ? 'Ordet blinker kort igen — find den rigtige tast'
                    : 'Find den rigtige tast, roligt'}
                </p>
              )}
            </>
          )
        ) : (
          <p className="text-lg font-light text-emerald-700/80 dark:text-emerald-300/80">
            Runden er færdig
          </p>
        )}

        {phase === 'dictation' && dictationStarted && currentWord && !awaitingSpace && (
          <button
            type="button"
            onClick={handleRepeatAudio}
            className="text-xs text-stone-500 underline-offset-2 hover:text-stone-700 hover:underline dark:text-stone-400 dark:hover:text-stone-200"
          >
            Hør ordet igen
          </button>
        )}
      </div>

      <ZenKeyboard
        layoutId={keyboardLayout}
        unlockedKeys={trin.unlockedKeys}
        enabled={
          !input.finished && (phase !== 'dictation' || dictationStarted)
        }
        nextKey={awaitingSpace ? ' ' : input.expectedChar}
        lastPress={input.lastPress}
      />

      <p className="text-center text-xs leading-relaxed text-faint">
        {phase === 'visible'
          ? 'Næste ord kommer efter mellemrum. Skriv når du er klar. Escape afslutter.'
          : phase === 'flash'
            ? 'Et sekunds blink, derefter prikker. Gentagelser: kun prikker. Mellemrum = næste.'
            : 'Lyt, forestil, skriv. Mellemrum mellem ord. Escape afslutter.'}
      </p>
    </ZenShell>
  );
}

function ZenShell({
  phaseLabel,
  children,
}: {
  phaseLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-8">
      <p className="text-center text-xs font-medium tracking-[0.2em] uppercase text-stone-400 dark:text-stone-500">
        {phaseLabel}
      </p>
      {children}
    </div>
  );
}

function ZenKeyboard({
  layoutId,
  unlockedKeys,
  enabled,
  nextKey,
  lastPress,
}: {
  layoutId: KeyboardLayoutId;
  unlockedKeys: string[];
  enabled: boolean;
  nextKey?: string;
  lastPress?: { key: string; correct: boolean };
}) {
  return (
    <div className={enabled ? '' : 'pointer-events-none opacity-40'}>
      <Keyboard
        layoutId={layoutId}
        unlockedKeys={unlockedKeys}
        nextExpectedKey={enabled ? nextKey : undefined}
        lastPressedKey={lastPress?.key}
        lastPressCorrect={lastPress?.correct}
        hintLevel="none"
        showFingerDot={false}
      />
    </div>
  );
}

function TypedFeedback({
  word,
  wordStart,
  cursor,
  correctMask,
  showReferenceAbove,
  audioHints = false,
}: {
  word: string;
  wordStart: number;
  cursor: number;
  correctMask: ReadonlyArray<boolean>;
  showReferenceAbove: boolean;
  audioHints?: boolean;
}) {
  const typedCount = [...word].filter(
    (_, i) => correctMask[wordStart + i],
  ).length;

  return (
    <div className="flex w-full flex-col items-center gap-1">
      {showReferenceAbove && (
        <p className="text-[10px] font-medium tracking-wide uppercase text-stone-400 dark:text-stone-500">
          Dit skriv
        </p>
      )}
      <p
        className={`font-mono font-medium leading-relaxed tracking-wide ${
          showReferenceAbove ? 'text-4xl' : 'text-5xl'
        }`}
        aria-live="polite"
        aria-label={`Skrevet ${typedCount} af ${word.length} bogstaver`}
      >
        {[...word].map((ch, i) => {
          const absIdx = wordStart + i;
          const typed = correctMask[absIdx];
          const isCursor = absIdx === cursor;
          const hintChar = audioHints && isDictationAudioHintChar(ch);
          if (typed) {
            return (
              <span
                key={i}
                className="text-emerald-600 dark:text-emerald-300"
              >
                {ch}
              </span>
            );
          }
          if (isCursor) {
            return (
              <span
                key={i}
                className="inline-block min-w-[0.6ch] border-l-2 border-stone-500 bg-stone-100 text-stone-400 dark:border-stone-400 dark:bg-stone-800/80 dark:text-stone-500"
              >
                {hintChar ? ch : '·'}
              </span>
            );
          }
          if (hintChar) {
            return (
              <span key={i} className="text-stone-400 dark:text-stone-500">
                {ch}
              </span>
            );
          }
          return (
            <span key={i} className="text-stone-300 dark:text-stone-600">
              ·
            </span>
          );
        })}
      </p>
    </div>
  );
}
